import {
  REVIEWABLE_DOCUMENT_CATEGORIES,
  REVIEWABLE_DOCUMENT_TYPES,
  REVIEWABLE_DOCUMENT_TYPE_PREFIX,
  ReviewableDocumentCategoryKey,
  ReviewableDocumentInput,
} from '@bordrio/shared';
import clsx from 'clsx';
import {
  Control,
  FieldPath,
  FieldPathValue,
  FieldValues,
  PathValue,
  UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactSelectInput } from '@src/components';
import ReviewableDocumentFileInput from '../ReviewableDocumentFileInput/ReviewableDocumentFileInput';

interface Props<
  ValueType extends FieldValues,
  PathType extends FieldPath<ValueType>,
> {
  className?: string;
  control: FieldPathValue<ValueType, PathType> extends ReviewableDocumentInput
    ? Control<ValueType>
    : never;
  name: PathType;
  category: ReviewableDocumentCategoryKey;
  setValue: UseFormSetValue<ValueType>;
}

function ReviewableDocumentCategoryInput<
  ValueType extends FieldValues,
  PathType extends FieldPath<ValueType>,
>({
  className,
  control,
  name,
  category,
  setValue,
}: Props<ValueType, PathType>): JSX.Element {
  const { t } = useTranslation();
  const manualTranslate = t;

  // a hack to allow proper typings for the control
  const controlTyped = control as unknown as Control<{
    prefix: ReviewableDocumentInput;
  }>;
  const prefix = name as 'prefix';

  const categoryConfig = REVIEWABLE_DOCUMENT_CATEGORIES[category];

  const categoryTypes = Object.values(REVIEWABLE_DOCUMENT_TYPES).filter(
    (type) => type.categories.includes(categoryConfig.id),
  );

  const documentOptions = categoryTypes.map((type) => ({
    label: manualTranslate(
      `${REVIEWABLE_DOCUMENT_TYPE_PREFIX}.${type.key}`,
      type.name,
    ),
    value: type.id,
  }));

  return (
    <div className={clsx('space-y-4', className)}>
      <ReactSelectInput.LabelledController
        label={t(
          'translation:reviewable_documents.document_type_label',
          'Document Type',
        )}
        options={documentOptions}
        control={controlTyped}
        name={`${prefix}.currentVersion.documentTypeId`}
        onChange={() => {
          setValue(
            `${prefix}.currentVersion.fileOne` as FieldPath<ValueType>,
            null as PathValue<ValueType, FieldPath<ValueType>>,
          );
          setValue(
            `${prefix}.currentVersion.fileTwo` as FieldPath<ValueType>,
            null as PathValue<ValueType, FieldPath<ValueType>>,
          );
        }}
      />
      <ReviewableDocumentFileInput
        horizontalLayout
        control={controlTyped}
        name={prefix}
      />
    </div>
  );
}

export default ReviewableDocumentCategoryInput;
